import { useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChoiceGroup } from '@/components/ui/choice-group';
import { Fieldset } from '@/components/ui/field';
import { SelectField } from '@/components/ui/select-field';
import { FORM_ERROR, errorMessage } from '@/features/auth/use-auth-error';
import { withArticle } from '@/lib/validation';

import { InfoDialog, type InfoTopic } from '../info-dialogs';

import { CHOICE_ERROR, YES_NO, toBool, yesNo, type StepProps } from './step-props';
import { useSaveStep } from './use-save-step';

interface WorkBackgroundForm {
  states: { state: string }[];
  unlicensed: boolean;
  licenseHit: 'Yes' | 'No' | '';
  otherStatesInterest: 'Yes' | 'No' | '';
  workAuthorized: 'Yes' | 'No' | '';
  previousWorkSetting: 'Yes' | 'No' | '';
  experience: string;
}

export function WorkBackgroundStep({ record, lookups, onNext }: StepProps) {
  const [info, setInfo] = useState<InfoTopic>();
  const save = useSaveStep('work_background', onNext);
  const stateOptions = lookups.stateLicensesHeld ?? [];
  const held = record.stateLicensesHeld ?? [];

  const {
    register,
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitted },
  } = useForm<WorkBackgroundForm>({
    defaultValues: {
      states: held.length ? held.map((s) => ({ state: s })) : [{ state: '' }],
      unlicensed: record.noStateLicenseNumber ?? false,
      licenseHit: yesNo(record.licenseHit) ?? '',
      otherStatesInterest: yesNo(record.otherStatesInterest) ?? '',
      workAuthorized: yesNo(record.workAuthorized) ?? '',
      previousWorkSetting: yesNo(record.previousWorkSetting) ?? '',
      experience: record.experience ?? '',
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'states' });

  const states = useWatch({ control, name: 'states' });
  const unlicensed = useWatch({ control, name: 'unlicensed' });
  const chosen = states.map((s) => s.state).filter(Boolean);
  const anyChosen = chosen.length > 0;

  const submit = (v: WorkBackgroundForm) => {
    const list = v.states.map((s) => s.state).filter(Boolean);
    if (!v.unlicensed && list.length === 0) {
      setError('states', { message: CHOICE_ERROR });
      return;
    }
    clearErrors('states');
    save.mutate({
      stateLicensesHeld: list.length ? list : null,
      noStateLicenseNumber: v.unlicensed,
      licenseHit: v.unlicensed ? null : toBool(v.licenseHit),
      otherStatesInterest: v.unlicensed ? null : toBool(v.otherStatesInterest),
      workAuthorized: toBool(v.workAuthorized),
      previousWorkSetting: toBool(v.previousWorkSetting),
      experience: v.experience,
    });
  };

  const hasFieldErrors = isSubmitted && Object.keys(errors).length > 0;
  const professionName = record.profession.name ?? 'a healthcare professional';

  return (
    <div className="stack stack--loose">
      <div>
        <h2>Tell us a bit about your work background...</h2>
        <hr className="divider" />
      </div>
      {save.isError && <Alert>{errorMessage(save.error)}</Alert>}
      {!save.isError && hasFieldErrors && <Alert>{FORM_ERROR}</Alert>}

      <form
        className="stack stack--loose"
        noValidate
        onSubmit={(e) => void handleSubmit(submit)(e)}
      >
        <Fieldset
          legend="In which states do you hold a license to work?"
          size="lg"
          error={errors.states?.message ?? errors.states?.root?.message}
          errorId="states-error"
        >
          <div className="repeater">
            {fields.map((field, i) => {
              const others = new Set(chosen.filter((s) => s !== states[i]?.state));
              return (
                <div key={field.id} className="repeater__row">
                  <SelectField
                    id={`step3-state-${i}`}
                    label={<span className="visually-hidden">State {i + 1}</span>}
                    options={stateOptions}
                    disabledValues={others}
                    disabled={unlicensed}
                    {...register(`states.${i}.state` as const)}
                  />
                  {!unlicensed && fields.length > 1 && (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        remove(i);
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              );
            })}
            {anyChosen ? (
              <div>
                <button
                  type="button"
                  className="link-button"
                  disabled={unlicensed}
                  onClick={() => {
                    append({ state: '' });
                  }}
                >
                  + Add another state
                </button>
              </div>
            ) : (
              <Checkbox label="I don't have a license" {...register('unlicensed')} />
            )}
          </div>
        </Fieldset>

        {!unlicensed && (
          <>
            <ChoiceGroup
              type="radio"
              name="licenseHit"
              legend="Has your Professional License (in any state) ever been in probationary status, revoked, or suspended?"
              inline
              options={YES_NO}
              error={errors.licenseHit?.message}
              registration={register('licenseHit', { required: CHOICE_ERROR })}
            />
            <ChoiceGroup
              type="radio"
              name="otherStatesInterest"
              legend={
                <>
                  Are you interested in working in any other states you are not currently licensed
                  in?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setInfo('otherStates');
                    }}
                  >
                    Learn More
                  </button>
                </>
              }
              inline
              options={YES_NO}
              error={errors.otherStatesInterest?.message}
              registration={register('otherStatesInterest', { required: CHOICE_ERROR })}
            />
          </>
        )}

        <ChoiceGroup
          type="radio"
          name="workAuthorized"
          legend="Are you at least 18 years old and legally authorized to work in the United States?"
          inline
          options={YES_NO}
          error={errors.workAuthorized?.message}
          registration={register('workAuthorized', { required: CHOICE_ERROR })}
        />
        <ChoiceGroup
          type="radio"
          name="previousWorkSetting"
          legend="Have you previously been employed by MAS?"
          inline
          options={YES_NO}
          error={errors.previousWorkSetting?.message}
          registration={register('previousWorkSetting', { required: CHOICE_ERROR })}
        />
        <SelectField
          id="step3-experience"
          label={`How many years of experience do you have working as ${withArticle(professionName)}?`}
          options={lookups.experience ?? []}
          error={errors.experience?.message}
          {...register('experience', { required: CHOICE_ERROR })}
        />

        <Button type="submit" busy={save.isPending}>
          Save &amp; continue
        </Button>
      </form>

      <InfoDialog
        topic={info}
        onClose={() => {
          setInfo(undefined);
        }}
      />
    </div>
  );
}
